import { Component } from '@theme/component';

/**
 * Quick filter chips under the collection's Filter / Sort bar (snippets/WYN-quick-filters)
 * and the phone sort control beside the Filter button (snippets/WYN-mobile-sort).
 *
 * Each chip is a real link to the filtered URL, so it works before this module loads.
 * Once loaded, a chip applies its filter through the theme's facets form, which updates
 * the grid without a page reload. The chips live inside the collection section, so they
 * re-render with it and their selected state follows every filter change.
 *
 * @extends {Component}
 */
/**
 * Runs a callback after the browser has painted the current frame.
 * @param {() => void} callback
 */
function afterNextPaint(callback) {
  requestAnimationFrame(() => setTimeout(callback, 0));
}

class QuickFiltersComponent extends Component {
  /**
   * Applies the clicked chip's filter through the facets form.
   * @param {MouseEvent} event - Click on a chip link
   */
  applyFilter(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    // Let modified clicks open a new tab as usual.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const facetsForm = /** @type {(HTMLElement & { updateFiltersByURL?: (url: string) => void }) | null} */ (
      document.querySelector('facets-form-component')
    );
    if (typeof facetsForm?.updateFiltersByURL !== 'function') return;

    event.preventDefault();
    link.setAttribute('aria-busy', 'true');
    // Let the tap paint first: the filter update re-renders the grid synchronously, which
    // would otherwise hold the next frame (slow INP).
    afterNextPaint(() => facetsForm.updateFiltersByURL(link.href));
  }

  /**
   * Changes only the sort, keeping every active filter (snippets/WYN-mobile-sort).
   * @param {Event} event - Change on the sort select
   */
  applySort(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');

    const facetsForm = /** @type {(HTMLElement & { updateFiltersByURL?: (url: string) => void }) | null} */ (
      document.querySelector('facets-form-component')
    );
    if (typeof facetsForm?.updateFiltersByURL === 'function') {
      afterNextPaint(() => facetsForm.updateFiltersByURL(url.toString()));
    } else {
      window.location.assign(url.toString());
    }
  }
}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}
